'use client';

import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const CommissionSection = () => {
  const { t } = useLanguage();
  const [showTable, setShowTable] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const packages = [
    {
      name: 'CUANTICO',
      price: '$777',
      commission: 'Up to 20%',
      commissionEs: 'Hasta 20%',
      highlight: false,
      color: '#4A5568',
      bgColor: '#F4F7FC',
    },
    {
      name: 'CRISTAL',
      price: '$999',
      commission: 'Up to 20%',
      commissionEs: 'Hasta 20%',
      highlight: false,
      color: '#1D6FE8',
      bgColor: '#EEF4FD',
    },
    {
      name: 'DIAMANTE',
      price: '$1,799',
      commission: 'Up to 20%',
      commissionEs: 'Hasta 20%',
      highlight: false,
      color: '#0A1628',
      bgColor: '#F4F7FC',
    },
    {
      name: 'ÉLITE',
      price: '$2,500',
      commission: 'Up to $500 USD',
      commissionEs: 'Hasta $500 USD',
      highlight: true,
      color: '#C9A84C',
      bgColor: '#FDF8EC',
    },
  ];

  const tableData = [
    { name: 'CUANTICO', price: '$777', tier1: '$77.70', tier2: '$116.55', tier3: '$155.40', highlight: false },
    { name: 'CRISTAL', price: '$999', tier1: '$99.90', tier2: '$149.85', tier3: '$199.80', highlight: false },
    { name: 'DIAMANTE', price: '$1,799', tier1: '$179.90', tier2: '$269.85', tier3: '$359.80', highlight: false },
    { name: 'ÉLITE', price: '$2,500', tier1: '$250', tier2: '$375', tier3: '$500', highlight: true },
  ];

  return (
    <section ref={sectionRef} id="comisiones" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
            <span className="text-sm font-body font-medium text-[#C9A84C] tracking-wide uppercase">
              {t('commission.eyebrow')}
            </span>
          </div>
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            {t('commission.headline')}{' '}
            <span className="gradient-text-gold">{t('commission.headlineAccent')}</span>
          </h2>
          <p className="font-body text-lg text-[#4A5568] leading-relaxed">
            {t('commission.subtitle')}
          </p>
        </div>

        {/* Package Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {packages?.map((pkg, index) => (
            <div
              key={pkg?.name}
              className={`relative rounded-2xl p-6 border transition-all duration-450 hover-lift ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${
                pkg?.highlight
                  ? 'bg-[#0A1628] border-[#0A1628]'
                  : 'bg-white border-[#E8EDF5]'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {pkg?.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#0A1628] text-xs font-display font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  Best Value
                </div>
              )}

              <div className="mb-4">
                <span className={`text-xs font-body font-semibold tracking-widest uppercase ${
                  pkg?.highlight ? 'text-[#C9A84C]' : 'text-[#4A5568]'
                }`}>
                  {pkg?.name}
                </span>
              </div>

              <div className={`text-3xl font-display font-bold mb-1 ${
                pkg?.highlight ? 'text-white' : 'text-[#0A1628]'
              }`}>
                {pkg?.price}
              </div>
              <div className={`text-xs font-body mb-6 ${
                pkg?.highlight ? 'text-white/60' : 'text-[#4A5568]'
              }`}>
                {t('commission.package')}
              </div>

              <div className={`text-lg font-display font-bold ${
                pkg?.highlight ? 'text-[#C9A84C]' : 'text-[#1D6FE8]'
              }`}>
                {pkg?.commission}
              </div>
              <div className={`text-xs font-body mt-0.5 ${
                pkg?.highlight ? 'text-white/60' : 'text-[#4A5568]'
              }`}>
                {t('commission.perSale')}
              </div>
            </div>
          ))}
        </div>

        {/* Highlight callout */}
        <div className={`flex items-center justify-center gap-3 mb-10 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-3 bg-[#FDF8EC] border border-[#C9A84C]/30 rounded-2xl px-6 py-4">
            <Icon name="StarIcon" size={20} className="text-[#C9A84C] flex-shrink-0" />
            <span className="font-display font-semibold text-[#0A1628] text-base">
              {t('commission.highlight')}
            </span>
          </div>
        </div>

        {/* Toggle Table Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowTable(!showTable)}
            className="inline-flex items-center gap-2 bg-[#0A1628] text-white font-display font-semibold text-base px-8 py-4 rounded-2xl hover:bg-[#1D6FE8] transition-all duration-350 shadow-cta"
          >
            {showTable ? t('commission.hideTable') : t('commission.viewFull')}
            <Icon name={showTable ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={18} />
          </button>
        </div>

        {/* Expandable Table */}
        {showTable && (
          <div className="animate-fade-up overflow-hidden rounded-2xl border border-[#E8EDF5] shadow-card">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F4F7FC]">
                  <th className="text-left px-6 py-4 font-display font-semibold text-sm text-[#0A1628]">
                    {t('commission.package')}
                  </th>
                  <th className="text-left px-6 py-4 font-display font-semibold text-sm text-[#0A1628]">
                    {t('commission.price')}
                  </th>
                  <th className="text-center px-6 py-4 font-display font-semibold text-sm text-[#0A1628]">
                    {t('commission.tier1')}
                  </th>
                  <th className="text-center px-6 py-4 font-display font-semibold text-sm text-[#0A1628]">
                    {t('commission.tier2')}
                  </th>
                  <th className="text-center px-6 py-4 font-display font-semibold text-sm text-[#0A1628]">
                    {t('commission.tier3')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData?.map((row, i) => (
                  <tr
                    key={row?.name}
                    className={`border-t border-[#E8EDF5] transition-colors ${
                      row?.highlight ? 'bg-[#FDF8EC]' : 'bg-white hover:bg-[#F4F7FC]'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className={`font-display font-bold text-sm tracking-wider ${
                        row?.highlight ? 'text-[#C9A84C]' : 'text-[#0A1628]'
                      }`}>
                        {row?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-body font-semibold text-[#0A1628]">{row?.price}</td>
                    <td className="px-6 py-4 text-center font-body text-[#4A5568]">{row?.tier1}</td>
                    <td className="px-6 py-4 text-center font-body text-[#4A5568]">{row?.tier2}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`font-display font-bold ${
                        row?.highlight ? 'text-[#C9A84C]' : 'text-[#1D6FE8]'
                      }`}>
                        {row?.tier3}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default CommissionSection;
